import { useEffect, useState } from "react";
import { Heart, MessageSquare, Send } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import FileDropzone, { resolveFileUrl } from "@/components/FileDropzone";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function PostCard({ post, onLike, onOpenComments }) {
  return (
    <article data-testid={`post-${post.id}`} className="border-t-2 border-black py-8">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 border border-black bg-[#F7931A]/20 flex items-center justify-center font-display font-black">
          {post.author?.avatar_url ? (
            <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span>{post.author?.name?.[0]?.toUpperCase()}</span>
          )}
        </div>
        <div>
          <div className="font-display font-bold text-lg">{post.author?.name}</div>
          <div className="font-mono text-xs text-[#0A0A0A]/60 uppercase">
            {timeAgo(post.created_at)} ago
          </div>
        </div>
      </div>
      <p className="text-lg leading-relaxed mt-4 whitespace-pre-wrap">{post.text}</p>
      {post.image_url && (
        <img
          src={post.image_url}
          alt=""
          className="mt-4 w-full border border-black object-cover max-h-[520px]"
        />
      )}
      <div className="mt-5 flex items-center gap-6 font-mono text-sm uppercase tracking-widest">
        <button
          onClick={() => onLike(post)}
          data-testid={`like-${post.id}`}
          className={`flex items-center gap-2 hover:text-[#F7931A] transition-colors ${
            post.liked_by_me ? "text-[#F7931A]" : ""
          }`}
        >
          <Heart className={`w-4 h-4 ${post.liked_by_me ? "fill-current" : ""}`} />
          {post.like_count}
        </button>
        <button
          onClick={() => onOpenComments(post)}
          data-testid={`comments-${post.id}`}
          className="flex items-center gap-2 hover:text-[#F7931A] transition-colors"
        >
          <MessageSquare className="w-4 h-4" />
          {post.comment_count}
        </button>
      </div>
    </article>
  );
}

function CommentDrawer({ post, onClose, onCommented }) {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!post) return;
    api.get(`/posts/${post.id}/comments`).then((r) => setComments(r.data));
  }, [post]);

  if (!post) return null;

  const send = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/posts/${post.id}/comment`, { text });
      setComments((c) => [...c, data]);
      setText("");
      onCommented(post.id);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-[#FDFBF7] border-2 border-black w-full max-w-lg max-h-[80vh] flex flex-col"
        data-testid="comments-drawer"
      >
        <div className="px-6 py-4 border-b-2 border-black flex items-center justify-between">
          <div className="font-display font-black uppercase">Comments</div>
          <button onClick={onClose} className="font-mono text-sm">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {comments.length === 0 && (
            <div className="text-sm text-[#0A0A0A]/60 font-mono">No comments yet.</div>
          )}
          {comments.map((c) => (
            <div key={c.id} className="border-l-2 border-[#F7931A] pl-3">
              <div className="font-display font-bold text-sm">{c.user_name}</div>
              <div className="text-sm mt-1">{c.text}</div>
            </div>
          ))}
        </div>
        <div className="p-4 border-t-2 border-black flex gap-2">
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            data-testid="comment-input"
            className="flex-1 px-3 py-2 bg-white border-2 border-black font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#F7931A]"
            placeholder="Say something..."
          />
          <button
            onClick={send}
            disabled={loading || !text.trim()}
            data-testid="comment-submit"
            className="px-4 bg-[#F7931A] border-2 border-black disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Feed() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [openPost, setOpenPost] = useState(null);

  const load = async () => {
    const { data } = await api.get("/posts");
    setPosts(data);
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/posts", { text, image_url: imageUrl || null });
      setPosts((p) => [data, ...p]);
      setText("");
      setImageUrl("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const like = async (post) => {
    try {
      const { data } = await api.post(`/posts/${post.id}/like`);
      setPosts((ps) =>
        ps.map((p) => (p.id === post.id ? { ...p, like_count: data.like_count, liked_by_me: data.liked_by_me } : p))
      );
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const bumpComment = (postId) => {
    setPosts((ps) => ps.map((p) => (p.id === postId ? { ...p, comment_count: p.comment_count + 1 } : p)));
  };

  return (
    <main className="max-w-2xl mx-auto px-4 py-10">
      <h1 className="font-display font-black text-4xl uppercase tracking-tighter">The Feed.</h1>
      <p className="mt-2 text-sm text-[#0A0A0A]/60 font-mono uppercase">
        Signed in as {user?.name}
      </p>

      <form onSubmit={submit} data-testid="post-composer" className="mt-8 border-2 border-black bg-white p-4">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="What's happening on-chain?"
          rows={3}
          data-testid="post-text"
          className="w-full px-3 py-2 bg-transparent border-b-2 border-black/10 font-body resize-none focus:outline-none focus:border-[#F7931A]"
        />
        {imageUrl && (
          <div className="mt-3">
            <FileDropzone
              value={imageUrl}
              onChange={setImageUrl}
              accept="image/*"
              label="Attach image"
              testId="post-image-upload"
              aspect="aspect-video max-h-[240px]"
            />
          </div>
        )}
        <div className="flex items-center gap-2 mt-3">
          <div className="flex-1">
            {!imageUrl && (
              <FileDropzone
                value={imageUrl}
                onChange={setImageUrl}
                accept="image/*"
                label="Attach image"
                testId="post-image-upload"
                aspect="h-14"
              />
            )}
          </div>
          <button
            type="submit"
            disabled={loading || !text.trim()}
            data-testid="post-submit"
            className="px-6 py-3 bg-[#F7931A] border-2 border-black font-bold uppercase tracking-widest text-sm btn-brutal disabled:opacity-50"
          >
            Post
          </button>
        </div>
      </form>

      <div className="mt-4">
        {posts.length === 0 && (
          <div className="py-10 text-center font-mono text-sm text-[#0A0A0A]/60">
            No posts yet. Be the first.
          </div>
        )}
        {posts.map((p) => (
          <PostCard key={p.id} post={p} onLike={like} onOpenComments={setOpenPost} />
        ))}
      </div>

      <CommentDrawer post={openPost} onClose={() => setOpenPost(null)} onCommented={bumpComment} />
    </main>
  );
}
